// frontend/src/components/production-sequence/ColumnFilterPopover.tsx
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { filterFieldCls, filterLabelCls } from '@/components/common/FilterBar';
import type { SortDirection } from '@/types/productionSequence';

interface Props {
  dark: boolean;
  colLabel: string;
  kind: 'text' | 'number' | 'set';
  currentValue: string;
  options?: { value: string; label: string }[];
  sortDir: SortDirection | null;
  anchorRect: DOMRect | null;
  onApply: (value: string) => void;
  onClear: () => void;
  onSort: (dir: SortDirection) => void;
  onClose: () => void;
}

const POPOVER_W = 240; // w-60

export function ColumnFilterPopover({
  dark, colLabel, kind, currentValue, options = [], sortDir, anchorRect, onApply, onClear, onSort, onClose,
}: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState(kind === 'text' ? currentValue : '');
  const [min, setMin] = useState(kind === 'number' && currentValue.includes('|') ? currentValue.split('|')[0] : '');
  const [max, setMax] = useState(kind === 'number' && currentValue.includes('|') ? currentValue.split('|')[1] : '');
  const [sel, setSel] = useState<Set<string>>(
    new Set(kind === 'set' && currentValue ? currentValue.split(',') : []),
  );

  // Cierra al hacer scroll (la posición fija quedaría desfasada) o al redimensionar.
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [onClose]);

  if (!anchorRect) return null;

  function apply() {
    if (kind === 'text') onApply(text.trim());
    else if (kind === 'number') onApply(min || max ? `${min}|${max}` : '');
    else onApply([...sel].join(','));
    onClose();
  }

  const card = dark ? 'bg-slate-900 border-white/10 text-slate-200' : 'bg-white border-slate-200 text-slate-700';
  const radioRow = 'flex items-center gap-1.5 text-[12px] cursor-pointer';

  // Posición fija anclada al botón; se ajusta para no salirse por la derecha.
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - POPOVER_W - 8));
  const top = anchorRect.bottom + 4;

  // Renderizado en portal para escapar del overflow/stacking de la tabla.
  return createPortal(
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} aria-hidden="true" />
      <div
        className={`fixed z-[61] w-60 rounded-xl border shadow-2xl p-3 ${card}`}
        style={{ top, left }}
        onClick={e => e.stopPropagation()}
      >
        <div className={`mb-2 ${filterLabelCls(dark)}`}>{t('sequence.filter.searchIn', { col: colLabel })}</div>

        {kind === 'text' && (
          <input
            autoFocus value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') apply(); }}
            className={filterFieldCls(dark, !!text)} placeholder={colLabel}
          />
        )}

        {kind === 'number' && (
          <div className="flex items-center gap-2">
            <input type="number" value={min} onChange={e => setMin(e.target.value)}
              className={filterFieldCls(dark, !!min)} placeholder={t('sequence.filter.min')} />
            <span className="opacity-40">–</span>
            <input type="number" value={max} onChange={e => setMax(e.target.value)}
              className={filterFieldCls(dark, !!max)} placeholder={t('sequence.filter.max')} />
          </div>
        )}

        {kind === 'set' && (
          <div className="max-h-40 overflow-y-auto flex flex-col gap-1">
            {options.map(opt => (
              <label key={opt.value} className={radioRow}>
                <input
                  type="checkbox" checked={sel.has(opt.value)}
                  onChange={() => setSel(prev => {
                    const n = new Set(prev);
                    if (n.has(opt.value)) n.delete(opt.value); else n.add(opt.value);
                    return n;
                  })}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {/* Orden */}
        <div className={`mt-3 mb-1 ${filterLabelCls(dark)}`}>{t('sequence.filter.sort')}</div>
        <div className="flex items-center gap-4">
          <label className={radioRow}>
            <input type="radio" name="sortdir" checked={sortDir === 'asc'} onChange={() => onSort('asc')} />
            <span>{t('sequence.filter.asc')}</span>
          </label>
          <label className={radioRow}>
            <input type="radio" name="sortdir" checked={sortDir === 'desc'} onChange={() => onSort('desc')} />
            <span>{t('sequence.filter.desc')}</span>
          </label>
        </div>

        <div className="flex items-center justify-between mt-3 pt-2 border-t border-current/10">
          <button onClick={() => { onClear(); onClose(); }}
            className={`text-[12px] ${dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}>
            {t('sequence.filter.clear')}
          </button>
          <button onClick={apply}
            className="text-[12px] font-semibold px-3 py-1 rounded-lg bg-sky-500 text-white hover:bg-sky-600 cursor-pointer">
            {t('sequence.filter.apply')}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
